import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type MatchPublicView, type RegisteredGame } from '../api-client.js';
import { useAuthStore } from '../auth/auth-store.js';
import { useEventStream } from '../streams/use-event-stream.js';

type StatusFilter = 'active' | 'completed' | 'all';

export default function MyMatchesPage() {
  const user = useAuthStore((s) => s.user);
  const [matches, setMatches] = useState<MatchPublicView[] | null>(null);
  const [games, setGames] = useState<RegisteredGame[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [gameFilter, setGameFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  const refresh = useCallback(() => {
    if (!user) return;
    api
      .listMyMatches(user.id)
      .then((data) => setMatches(data))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [user]);

  useEffect(() => {
    refresh();
    api
      .listGames()
      .then((data) => setGames(data))
      .catch(() => {
        // Filter still works without metadata.
      });
  }, [refresh]);

  // Live updates: server pushes on every match-list change for this
  // user (created, joined, opponent acted, status flipped).
  useEventStream(user ? `/api/streams/users/${user.id}/matches` : null, () => {
    refresh();
  });

  const filtered = useMemo(() => {
    if (!matches) return [];
    const term = search.trim().toLowerCase();
    return matches.filter((m) => {
      // Status filter.
      if (statusFilter === 'completed' && m.match.status !== 'completed') return false;
      if (
        statusFilter === 'active' &&
        m.match.status !== 'waiting_for_players' &&
        m.match.status !== 'in_progress'
      ) {
        return false;
      }
      // Game filter.
      if (gameFilter !== 'all' && m.match.gameKey !== gameFilter) return false;
      // Free-text search across match id, game name, and opponent
      // user-id prefix. Cheap, all-local — no extra fetches.
      if (term.length > 0) {
        const haystack = [
          m.match.id,
          m.game.name,
          m.match.gameKey,
          ...m.participants.map((p) => p.userId),
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [matches, statusFilter, gameFilter, search]);

  const gameKeysInList = useMemo(() => {
    if (!matches) return new Set<string>();
    return new Set(matches.map((m) => m.match.gameKey));
  }, [matches]);

  if (!user) {
    return <p className="text-ec-bone">Log in to see your matches.</p>;
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-ec-face">Your matches</h2>
        <Link
          to="/"
          className="ml-auto font-display text-sm uppercase tracking-wide text-ec-brass hover:underline"
        >
          ← back to games
        </Link>
      </header>

      {error && (
        <div className="ec-surface p-3 text-sm text-ec-face">
          {error}
        </div>
      )}

      {!matches && !error && (
        <p className="text-sm text-ec-bone-dim">Loading your matches…</p>
      )}

      {matches && (
        <>
          <div className="flex flex-wrap gap-2">
            {(['active', 'completed', 'all'] as StatusFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                className={`ec-btn !py-1.5 text-sm ${statusFilter === f ? '!border-ec-brass !text-ec-brass' : ''}`}
              >
                {f}
              </button>
            ))}
            <span className="ml-auto self-center text-xs text-ec-bone-dim">
              {filtered.length} of {matches.length} matches
            </span>
          </div>

          {/* Game-filter chips only render when the user has matches
              across more than one game — saves screen space when the
              chips would all be the same value. */}
          {games && gameKeysInList.size > 1 && (
            <div className="flex flex-wrap gap-2">
              <GameChip
                label="All games"
                active={gameFilter === 'all'}
                onClick={() => setGameFilter('all')}
              />
              {games
                .filter((g) => gameKeysInList.has(g.key))
                .map((g) => (
                  <GameChip
                    key={g.key}
                    label={g.name}
                    active={gameFilter === g.key}
                    onClick={() => setGameFilter(g.key)}
                  />
                ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by match id, game, or opponent…"
              className="block min-h-[40px] flex-1 rounded-md border border-ec-line bg-ec-ground px-3 py-1.5 text-base text-ec-face placeholder-ec-bone-dim focus:border-ec-brass focus:outline-none focus:ring-1 focus:ring-ec-brass"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="ec-btn !py-1.5 text-sm"
              >
                Clear
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-ec-bone-dim">
              {matches.length === 0
                ? "You haven't played any matches yet."
                : 'No matches match these filters.'}
            </p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((m) => (
                <MatchListCard key={m.match.id} match={m} youUserId={user.id} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function GameChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ec-btn !py-1.5 text-sm ${active ? '!border-ec-brass !text-ec-brass' : ''}`}
    >
      {label}
    </button>
  );
}

function MatchListCard({
  match,
  youUserId,
}: {
  match: MatchPublicView;
  youUserId: string;
}) {
  const youArePlayer = match.participants.some((p) => p.userId === youUserId);
  const youAreCreator = match.match.createdByUserId === youUserId;
  const opponent = match.participants.find((p) => p.userId !== youUserId);
  const status = match.match.status;
  const statusBadge = STATUS_BADGE[status] ?? STATUS_BADGE.unknown;
  const updated = match.match.startedAt ?? match.match.createdAt;

  return (
    <li className="ec-surface flex flex-col p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="truncate font-display text-base font-medium uppercase tracking-wide text-ec-face">
          {match.game.name}
        </h3>
        <span className={`rounded-full border px-2 py-0.5 font-display text-[10px] font-semibold uppercase tracking-wider ${statusBadge}`}>
          {status.replace(/_/g, ' ')}
        </span>
      </div>
      <p className="mt-1 truncate font-mono text-xs text-ec-bone-dim" title={match.match.id}>
        {match.match.id}
      </p>
      <dl className="mt-3 space-y-1 text-xs">
        <div className="flex justify-between">
          <dt className="text-ec-bone-dim">Role</dt>
          <dd className="text-ec-bone">
            {youArePlayer ? 'Player' : 'Creator'}
            {youAreCreator && youArePlayer && ' · created'}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ec-bone-dim">Opponent</dt>
          <dd className="truncate text-ec-bone">
            {opponent ? opponent.userId.slice(0, 12) : '—'}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ec-bone-dim">Started</dt>
          <dd className="text-ec-bone">
            {new Date(updated).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </dd>
        </div>
      </dl>
      <Link
        to={`/matches/${match.match.id}`}
        className="ec-btn ec-btn--primary mt-4 w-full"
      >
        {status === 'waiting_for_players'
          ? 'Open lobby'
          : status === 'in_progress'
            ? 'Continue'
            : 'View'}
      </Link>
    </li>
  );
}

const STATUS_BADGE: Record<string, string> = {
  waiting_for_players: 'border-ec-brass/60 bg-[rgba(245,168,28,0.18)] text-ec-brass',
  in_progress: 'border-ec-defence bg-[rgba(85,167,47,0.18)] text-ec-go',
  completed: 'border-ec-line bg-ec-ground text-ec-bone-dim',
  unknown: 'border-ec-line bg-ec-ground text-ec-bone-dim',
};
